import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  signInWithGoogle,
  signUpWithEmail,
  signInWithEmail,
  sendPasswordReset,
  resendVerificationEmail,
} from '../services/firebase';
import './AuthModal.css';

const FEATURES = [
  '1 free credit on sign-up (= 1 tracking)',
  'Real-time tracking history & status',
  'Developer API access with your own key',
];

/** Firebase error codes carry no user-facing copy, so map the ones users hit. */
const FRIENDLY_ERRORS = {
  'auth/email-already-in-use': 'That email address already has an account. Try signing in instead.',
  'auth/invalid-email': 'That does not look like a valid email address.',
  'auth/weak-password': 'Choose a password of at least 6 characters.',
  'auth/invalid-credential': 'Incorrect email address or password.',
  'auth/wrong-password': 'Incorrect email address or password.',
  'auth/user-not-found': 'Incorrect email address or password.',
  'auth/too-many-requests': 'Too many attempts. Wait a moment and try again.',
  'auth/network-request-failed': 'Please check your network and try again.',
};

const messageFor = (err, fallback) => FRIENDLY_ERRORS[err?.code] || fallback;

const MODE_COPY = {
  signin: { title: 'Welcome back', subtitle: 'Sign in to continue tracking.', submit: 'Sign in' },
  signup: {
    title: 'Create your account',
    subtitle: 'Free to start — 1 credit included, and your own developer API key.',
    submit: 'Create account',
  },
  reset: {
    title: 'Reset your password',
    subtitle: 'We will email you a link to choose a new password.',
    submit: 'Send reset link',
  },
};

export default function AuthModal() {
  const {
    isAuthModalOpen,
    closeAuthModal,
    login,
    user,
    isEmailVerified,
    recheckEmailVerification,
    logout,
  } = useAuth();

  const [mode, setMode] = useState('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  // Set after signup, so the modal explains what to do next instead of
  // dropping the user into an account that cannot track anything yet.
  const [awaitingVerification, setAwaitingVerification] = useState(false);

  if (!isAuthModalOpen) return null;

  // A signed-in but unverified account is stuck until it confirms its address.
  const showVerifyStep = awaitingVerification || Boolean(user && !isEmailVerified);

  const resetFeedback = () => {
    setError('');
    setNotice('');
  };

  const switchMode = (next) => {
    resetFeedback();
    setMode(next);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) closeAuthModal();
  };

  const handleGoogleSignIn = async () => {
    resetFeedback();
    setIsBusy(true);
    try {
      const firebaseUser = await signInWithGoogle();
      await login(firebaseUser);
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setError(messageFor(err, 'Sign-in failed. Please try again.'));
        console.error('Firebase sign-in error:', err);
      }
    } finally {
      setIsBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    resetFeedback();
    setIsBusy(true);

    try {
      if (mode === 'reset') {
        await sendPasswordReset(email.trim());
        setNotice(`If an account exists for ${email.trim()}, a reset link is on its way.`);
      } else if (mode === 'signup') {
        await signUpWithEmail(email.trim(), password, name.trim());
        setAwaitingVerification(true);
      } else {
        const firebaseUser = await signInWithEmail(email.trim(), password);
        if (!firebaseUser.emailVerified) {
          setAwaitingVerification(true);
        } else {
          await login(firebaseUser);
        }
      }
    } catch (err) {
      setError(messageFor(err, 'Something went wrong. Please try again.'));
      console.error(`Auth ${mode} error:`, err);
    } finally {
      setIsBusy(false);
    }
  };

  const handleResend = async () => {
    resetFeedback();
    setIsBusy(true);
    try {
      await resendVerificationEmail();
      setNotice('Verification email sent. Check your inbox and spam folder.');
    } catch (err) {
      setError(messageFor(err, 'Could not resend the email. Please try again.'));
    } finally {
      setIsBusy(false);
    }
  };

  const handleVerifiedCheck = async () => {
    resetFeedback();
    setIsBusy(true);
    try {
      const verified = await recheckEmailVerification();
      if (verified) {
        setAwaitingVerification(false);
      } else {
        setError('Not verified yet. Click the link in the email, then try again.');
      }
    } catch (err) {
      setError('Could not check verification status. Please try again.');
    } finally {
      setIsBusy(false);
    }
  };

  const handleUseAnotherAccount = async () => {
    setAwaitingVerification(false);
    resetFeedback();
    setPassword('');
    if (user) await logout();
    setMode('signin');
  };

  /* ── Verification step ──────────────────────────────────────────────────── */
  if (showVerifyStep) {
    const pendingEmail = user?.email || email.trim();
    return (
      <div className="auth-overlay" onClick={handleOverlayClick}>
        <div className="auth-modal" role="dialog" aria-modal="true" aria-label="Verify your email">
          <button className="auth-modal-close" onClick={closeAuthModal} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>

          <div className="auth-brand">
            <img src="/Logo-01.png" alt="Ridelink" className="auth-brand-logo" />
          </div>

          <h2 className="auth-title">Check your inbox</h2>
          <p className="auth-subtitle">
            We sent a verification link to <strong>{pendingEmail}</strong>. Click it to activate
            tracking and API access on your account.
          </p>

          {notice && <p className="auth-notice">{notice}</p>}
          {error && <p className="auth-error">{error}</p>}

          <button className="auth-submit-btn" onClick={handleVerifiedCheck} disabled={isBusy}>
            {isBusy ? 'Checking…' : "I've verified my email"}
          </button>

          <div className="auth-alt-actions">
            <button type="button" className="auth-link-btn" onClick={handleResend} disabled={isBusy}>
              Resend email
            </button>
            <button type="button" className="auth-link-btn" onClick={handleUseAnotherAccount}>
              Use a different account
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Sign in / sign up / reset ──────────────────────────────────────────── */
  const copy = MODE_COPY[mode];

  return (
    <div className="auth-overlay" onClick={handleOverlayClick}>
      <div className="auth-modal" role="dialog" aria-modal="true" aria-label={copy.title}>
        <button className="auth-modal-close" onClick={closeAuthModal} aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        <div className="auth-brand">
          <img src="/Logo-01.png" alt="Ridelink" className="auth-brand-logo" />
        </div>

        <h2 className="auth-title">{copy.title}</h2>
        <p className="auth-subtitle">{copy.subtitle}</p>

        {mode === 'signup' && (
          <ul className="auth-features">
            {FEATURES.map((feat) => (
              <li key={feat}>
                <span className="auth-feature-check">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                {feat}
              </li>
            ))}
          </ul>
        )}

        <form className="auth-form" onSubmit={handleSubmit}>
          {mode === 'signup' && (
            <label className="auth-field">
              <span className="auth-field-label">Name</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                placeholder="Ada Lovelace"
              />
            </label>
          )}

          <label className="auth-field">
            <span className="auth-field-label">Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              placeholder="you@company.com"
            />
          </label>

          {mode !== 'reset' && (
            <label className="auth-field">
              <span className="auth-field-label">Password</span>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                placeholder={mode === 'signup' ? 'At least 6 characters' : '••••••••'}
              />
            </label>
          )}

          {notice && <p className="auth-notice">{notice}</p>}
          {error && <p className="auth-error">{error}</p>}

          <button type="submit" className="auth-submit-btn" disabled={isBusy}>
            {isBusy ? 'Please wait…' : copy.submit}
          </button>
        </form>

        <div className="auth-alt-actions">
          {mode === 'signin' && (
            <>
              <button type="button" className="auth-link-btn" onClick={() => switchMode('reset')}>
                Forgot password?
              </button>
              <button type="button" className="auth-link-btn" onClick={() => switchMode('signup')}>
                Create an account
              </button>
            </>
          )}
          {mode === 'signup' && (
            <button type="button" className="auth-link-btn" onClick={() => switchMode('signin')}>
              Already have an account? Sign in
            </button>
          )}
          {mode === 'reset' && (
            <button type="button" className="auth-link-btn" onClick={() => switchMode('signin')}>
              Back to sign in
            </button>
          )}
        </div>

        <div className="auth-divider"><span>or</span></div>

        <button
          className={`auth-google-btn${isBusy ? ' signing-in' : ''}`}
          onClick={handleGoogleSignIn}
          disabled={isBusy}
          type="button"
        >
          {isBusy ? (
            <span className="auth-google-btn-loading">
              <span className="auth-spinner" />
              Please wait…
            </span>
          ) : (
            <>
              <svg className="auth-google-icon" viewBox="0 0 24 24" width="20" height="20">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </>
          )}
        </button>

        <p className="auth-terms">
          By continuing you agree to our{' '}
          <a href="#terms" onClick={e => e.preventDefault()}>Terms of Service</a> and{' '}
          <a href="#privacy" onClick={e => e.preventDefault()}>Privacy Policy</a>
        </p>
      </div>
    </div>
  );
}
